import { Node, mergeAttributes } from '@tiptap/core';

// an org-authored placeholder chip: it carries only a name, and staff fill in
// its value when the agreement is sent
export const CustomVariableNode = Node.create({
  name: 'customVariable',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      name: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-custom-variable') || '',
        renderHTML: (attributes) => ({ 'data-custom-variable': attributes.name }),
      },
      // an optional field needs no value when sending; while blank, the line
      // holding it is left out of the document
      optional: {
        default: false,
        parseHTML: (element) => element.getAttribute('data-custom-variable-optional') === 'true',
        renderHTML: (attributes) =>
          attributes.optional ? { 'data-custom-variable-optional': 'true' } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-custom-variable]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, { class: 'agreement-variable' }),
      `${node.attrs.name || 'Custom Field'}${node.attrs.optional ? ' (optional)' : ''}`,
    ];
  },
});

export type CustomVariable = {
  name: string;
  optional: boolean;
};

// mirrors CollectCustomVariables in backend/app/utils/rentals_pdf — the custom
// variables of a template, in order and without duplicates, so the send dialog
// can ask for each value. A name placed both as required and optional counts
// as required.
export function collectCustomVariables(template: string): CustomVariable[] {
  if (!template) {
    return [];
  }
  let document: any;
  try {
    document = JSON.parse(template);
  } catch {
    return [];
  }
  const variables: CustomVariable[] = [];
  const indexByName = new Map<string, number>();
  const walk = (node: any) => {
    if (!node) {
      return;
    }
    if (node.type === 'customVariable') {
      const name = node.attrs?.name || '';
      const optional = Boolean(node.attrs?.optional);
      if (name) {
        const index = indexByName.get(name);
        if (index === undefined) {
          indexByName.set(name, variables.length);
          variables.push({ name, optional });
        } else {
          variables[index].optional = variables[index].optional && optional;
        }
      }
    }
    (node.content || []).forEach(walk);
  };
  walk(document);
  return variables;
}
